import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import TaskFormModal from "./KanbanComponents/TaskFormModal";
import { format, parseISO, isWithinInterval, isSameDay } from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  Trash,
  User,
  CircleDashed,
  Clock,
  ListChecks,
  ClipboardList,
  Save,
  LayoutGrid,
} from "lucide-react";
import { FormControl, InputLabel, Select, MenuItem, Divider } from "@mui/material";
import { getToken, base_url } from "../../config/config";

const WEEK_DAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const processColors = {
  assigned: {
    bg: "#475569",
    bg2: "bg-blue-500",
    text: "#ffffff",
    light: "#f1f5f9",
    icon: <ClipboardList className="w-5 h-5" />,
  },
  "in-progress": {
    bg: "#475569",
    bg2: "bg-yellow-600",
    text: "#ffffff",
    light: "#cffafe",
    icon: <Clock className="w-4 h-4" />,
  },
  completed: {
    bg: "#be185d",
    bg2: "bg-green-600",
    text: "#ffffff",
    light: "#fce7f3",
    icon: <Save className="w-4 h-4" />,
  },
};

const ProcessTooltip = ({ item, visible, x, y }) => {
  if (!visible || !item) return null;

  const processColor = processColors[item.status || "initiated"];

  return (
    <div
      className="fixed transform -translate-x-1/2 transition-all duration-200 ease-out pointer-events-none z-40"
      style={{
        top: y - 10,
        left: x,
      }}
    >
      <div className="bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden w-64 backdrop-blur-sm bg-white/95">
        {/* Tooltip arrow */}
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45 bg-white border-b border-r border-gray-100" />

        {/* Content container */}
        <div className="relative p-3">
          {/* Header */}
          <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
            <div
              className="p-1.5 rounded-lg"
              style={{ backgroundColor: processColor.light }}
            >
              {processColor.icon}
            </div>
            <div>
              <span className="font-semibold text-sm text-gray-900">
                {item.title}
              </span>
              <div className="text-xs text-gray-800">{item.description}</div>
              <div className="text-xs text-gray-500">Click for details</div>
            </div>
          </div>

          {/* Body */}
          <div className="space-y-2 mt-2">
            <div className="flex items-center gap-2">
              <CircleDashed className="w-4 h-4 text-gray-500" />
              <span className="text-xs text-gray-800 trucate max-w-[180px]">
                {item.status}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-500" />
              <span className="text-xs text-gray700 truncate max-w-[180px]">
                {item.user?.firstName && item.User?.lastName
                  ? item.names.firstName + " " + item.names.lastName
                  : "Kein Benutzer Assigned"}
              </span>
            </div>

            <div className="flex items-start gap-2">
              <ListChecks className="w-4 h-4 text-gray-500 mt-0.5" />
              <span className="text-xs text-gray-800 trunate max-w-[180px]">
                Subtasks {item.subTasks?.length}
              </span>
            </div>

            <div className="flex items-start gap-2">
              <Clock className="w-4 h-4 text-gray-500 mt-0.5" />
              <span className="text-xs text-gray-800 truncate max-w-[180px]">
                Created On {item.createdAt}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProcessCard = ({
  item,
  projectData,
  onDelete,
  onHover,
  onLeave,
  onSelect,
  isInGanttView = false,
  ...props
}) => {
  const processColor = processColors[item.status || "initiated"];

  return (
    <div
      {...props}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.(item);
      }}
      onMouseEnter={(e) => onHover?.(item, e.clientX, e.clientY)}
      onMouseLeave={() => onLeave?.()}
      className={`group cursor-pointer rounded-lg relative overflow-visible transition-all duration-300 hover:shadow-lg hover:translate-y-px transform-gpu min-w-[120px] shadow-sm" ${
        props.className || ""
      }`}
      style={{
        ...props.style,
        backgroundColor: processColor.bg,
        color: processColor.text,
      }}
    >
      <div className="relative flex flex-col h-full p-2">
        <div className="flex justify-between items-start gap-1">
          <div className="flex items-center gap-1 min-w-0 flex-shrink">
            <span className="font-semibold text-xs leading-tight truncate">
              {item.title}
            </span>
          </div>
          <div className="absolute -right-1 -top-1 flex gap-1 z-10">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(item.id);
              }}
              className="opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900/90 hover:bg-gray-900 rounded-full p-1.5 shadow-lg"
            >
              <Trash className="w-3 h-3 text-white" />
            </button>
          </div>
        </div>
        <span
          className={`self-start mt-1 px-1 py-0.5 text-[10px] text-white rounded ${processColor.bg2}`}
        >
          {item.status}
        </span>
      </div>
    </div>
  );
};

const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

const getDaysInMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();

  // Determine how many days to pad from the previous month
  const firstWeekday = new Date(year, month, 1).getDay();
  const paddingDays = firstWeekday === 0 ? 6 : firstWeekday - 1;

  const prevMonthLast = new Date(year, month, 0).getDate();
  const prevDays = Array.from(
    { length: paddingDays },
    (_, i) => new Date(year, month - 1, prevMonthLast - paddingDays + i + 1)
  );

  // All days in the current month
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const currDays = Array.from(
    { length: daysInMonth },
    (_, i) => new Date(year, month, i + 1)
  );

  const totalDaysSoFar = prevDays.length + currDays.length;
  // Only use 6 weeks (42 cells) if needed; otherwise 5 weeks (35 cells)
  const totalSlots = totalDaysSoFar > 35 ? 42 : 35;
  const remaining = totalSlots - totalDaysSoFar;

  const nextDays = Array.from(
    { length: remaining },
    (_, i) => new Date(year, month + 1, i + 1)
  );

  return [...prevDays, ...currDays, ...nextDays];
};

export default function CalendarView({
  onDelete = () => {},
}) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [taskFormOpen, setTaskFormOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [tooltipInfo, setTooltipInfo] = useState({
    visible: false,
    item: null,
    x: 0,
    y: 0,
  });
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserFilter, setSelectedUserFilter] = useState("");
  const [loggedInUserId, setLoggedInUserId] = useState("");
  const token = getToken();

  const handleHover = (item, x, y) => {
    if (item) {
      setTooltipInfo({
        visible: true,
        item,
        x,
        y,
      });
    }
  };

  const handleMouseLeave = () => {
    setTooltipInfo((prev) => ({
      ...prev,
      visible: false,
    }));
  };

  // Fetch tasks
  const fetchTasks = async () => {
    try {
      const params = { isTask: true };
      if (selectedUserFilter) params.user = selectedUserFilter;
      const response = await axios.get(`${base_url}/v1/getAllAufgabe`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      const data = Array.isArray(response.data) ? response.data : [];
      setTasks(data);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
  };

  // Fetch all users (Admins + Employees)
  const fetchUsers = async () => {
    try {
      const [adminRes, empRes] = await Promise.all([
        axios.get(`${base_url}/v1/getAllAdmins`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${base_url}/v1/getAllEmployees`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const adminsRaw = adminRes.data;
      const empsRaw = empRes.data;

      const adminList = Array.isArray(adminsRaw.users) ? adminsRaw.users : [];
      const employeeList = Array.isArray(empsRaw) ? empsRaw : [];

      const admins = adminList.map((adm) => ({
        _id: adm._id,
        displayName: `${adm.firstName} ${adm.lastName}`,
        userModel: "Admin",
      }));

      const employees = employeeList.map((emp) => ({
        _id: emp._id,
        displayName: emp.name,
        userModel: "Employee",
      }));

      setAllUsers([...admins, ...employees]);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  // Get logged in user ID from token
  useEffect(() => {
    try {
      const tokenPayload = token ? JSON.parse(atob(token.split(".")[1])) : null;
      if (tokenPayload?._id) {
        setLoggedInUserId(tokenPayload._id);
      }
    } catch (e) {
      console.error("Error parsing token:", e);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [selectedUserFilter]);

  const goToPreviousMonth = () =>
    setCurrentDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const goToNextMonth = () =>
    setCurrentDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  const goToCurrentMonth = () => setCurrentDate(new Date());
  const formatMonthYear = (date) => format(date, "LLLL yyyy");

  const monthGrid = useMemo(() => getDaysInMonth(currentDate), [currentDate]);

  const handleUserFilterChange = (e) => {
    setSelectedUserFilter(e.target.value);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
      {/* Top Bar: Navigation Link & User Filter */}
      <div className="flex gap-4 items-center mb-4 pb-4 border-b border-gray-200">
        <Link
          to="/MainAufgabeBoard"
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2 text-lg font-medium text-gray-600 hover:text-blue-600"
          title="zurück zu Kanban"
        >
          <LayoutGrid className="w-5 h-5" />
          <span>Zurück zu Kanban</span>
        </Link>

        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel id="user-filter-label">Benutzer</InputLabel>
          <Select
            labelId="user-filter-label"
            value={selectedUserFilter}
            label="Benutzer"
            onChange={handleUserFilterChange}
          >
            <MenuItem value={loggedInUserId}>Meine Aufgaben</MenuItem>
            <MenuItem value="">Alle Aufgaben</MenuItem>
            <Divider />
            {allUsers.map((u) => (
              <MenuItem key={u._id} value={u._id}>
                {u.displayName} ({u.userModel})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      {/* Calendar Navigation */}
      <div className="flex-shrink-0 flex items-center justify-center gap-4 mb-4">
        <button
          onClick={goToPreviousMonth}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Previous month"
        >
          <ChevronLeft className="w-6 h-6 text-gray-600" />
        </button>
        <div className="flex-1 text-center">
          <div className="text-xl font-semibold text-gray-900">
            {formatMonthYear(currentDate)}
          </div>
          <button
            onClick={goToCurrentMonth}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium mt-1"
          >
            Today
          </button>
        </div>
        <button
          onClick={goToNextMonth}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Next month"
        >
          <ChevronRight className="w-6 h-6 text-gray-600" />
        </button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-7 gap-2 md:gap-4">
        {WEEK_DAYS.map((day) => (
          <div
            key={day}
            className={`text-center font-semibold p-3 rounded-lg ${
              day === "Sa" || day === "So"
                ? "bg-gray-100 text-gray-500"
                : "bg-blue-50 text-blue-900"
            }`}
          >
            {day}
          </div>
        ))}
        {monthGrid.map((date) => {
          const iso = date.toISOString().split("T")[0];
          const weekend = isWeekend(date);
          const inMonth = date.getMonth() === currentDate.getMonth();

          const dayItems = !weekend
            ? tasks.filter((task) => {
                // Use startTime, or fall back to assignedDate, or deadline
                const startDateStr = task.startTime || task.assignedDate || task.deadline;
                if (!startDateStr) return false;
                
                const start = parseISO(startDateStr);
                // Use endTime if available, otherwise use deadline, or fall back to start
                const endDateStr = task.endTime || task.deadline;
                const end = endDateStr ? parseISO(endDateStr) : start;
                
                // Handle case where end is before start
                const effectiveEnd = end < start ? start : end;
                
                return (
                  isSameDay(date, start) ||
                  (effectiveEnd > start && isWithinInterval(date, { start, end: effectiveEnd }))
                );
              })
            : [];


            console.log("Tasks on ", iso, dayItems);

          const height =
            dayItems.length > 0
              ? `${Math.max(28, Math.min(36, 120 / dayItems.length))}px`
              : "36px";
          const today = isSameDay(date, new Date());

          return (
            <div
              key={iso}
              className={`border rounded-lg min-h-[160px] relative transition-colors overflow-visible ${
                today
                  ? "border-blue-300 ring-1 ring-blue-200"
                  : "border-gray-200"
              } ${
                weekend
                  ? "bg-gray-50 cursor-not-allowed"
                  : inMonth
                  ? "bg-white hover:bg-gray-50"
                  : "bg-gray-50"
              }`}
              onDragOver={(e) => !weekend && e.preventDefault()}
            >
              <div
                className={`text-sm p-2 font-medium ${
                  inMonth
                    ? weekend
                      ? "text-gray-400"
                      : today
                      ? "text-blue-600"
                      : "text-gray-900"
                    : "text-gray-400"
                }`}
              >
                {date.getDate()}
              </div>
              <div className="px-1 space-y-1 relative">
                {dayItems.map((item) => (
                  <ProcessCard
                    key={item._id}
                    item={item}
                    projectData={item.user || {}}
                    style={{ height }}
                    onDelete={onDelete}
                    onHover={handleHover}
                    onLeave={handleMouseLeave}
                    onSelect={() => {
                      setCurrentTask(item);
                      setTaskFormOpen(true);
                    }}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <ProcessTooltip
        item={tooltipInfo.item}
        visible={tooltipInfo.visible}
        x={tooltipInfo.x}
        y={tooltipInfo.y}
      />

      {/* TASK FORM MODAL */}
      <TaskFormModal
        open={taskFormOpen}
        onClose={() => setTaskFormOpen(false)}
        onSave={() => {}}
        initialTask={currentTask}
        columnId={0}
        allUsers={[]} // we pass allUsers => can pick any Admin/Employee
        isDarkMode={false}
      />
    </div>
  );
}
